import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import Hls from 'hls.js'
import VideoModal from '../../../components/home/VideoModal'
import VideoControls from '../../../components/home/VideoControls'
import { getVideoSources, initHLSOnVideo } from '../../../utils/videoUtils'
import { VideoSources } from '../../../types/video'
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver'

// Generate video sources for trusted section videos
const VIDEOS: VideoSources[] = [
    getVideoSources('trusted', 1),
    getVideoSources('trusted', 2),
]

const TrustedSection = () => {
    const { t } = useTranslation('home')
    const [activeIndex, setActiveIndex] = useState(0)
    const [playingIndex, setPlayingIndex] = useState<number | null>(null)
    const [progress, setProgress] = useState<number[]>([0, 0])
    const [loadingStates, setLoadingStates] = useState<boolean[]>([false, false])
    const [loadedStates, setLoadedStates] = useState<boolean[]>([false, false])
    const [modalOpen, setModalOpen] = useState(false)
    const [modalVideoSources, setModalVideoSources] = useState<VideoSources | null>(null)
    const [modalStartTime, setModalStartTime] = useState(0)
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
    const hlsRefs = useRef<(Hls | null)[]>([null, null])

    // Use intersection observer to pause videos when section is out of view
    const { ref: sectionRef, isVisible } = useIntersectionObserver<HTMLElement>({
        threshold: 0.1,
        rootMargin: '0px',
    })

    // Pause playing video when section leaves viewport
    useEffect(() => {
        if (!isVisible && playingIndex !== null) {
            videoRefs.current[playingIndex]?.pause()
            setPlayingIndex(null)
        }
    }, [isVisible, playingIndex])

    // Cleanup HLS instances on unmount
    useEffect(() => {
        return () => {
            hlsRefs.current.forEach(hls => hls?.destroy())
        }
    }, [])

    // Load video source with HLS (or fallback)
    const loadVideoSource = useCallback((index: number) => {
        const video = videoRefs.current[index]
        if (!video || loadedStates[index]) return

        // Initialize HLS with automatic fallback
        hlsRefs.current[index] = initHLSOnVideo(video, VIDEOS[index])
        setLoadedStates(prev => {
            const newStates = [...prev]
            newStates[index] = true
            return newStates
        })
    }, [loadedStates])

    useEffect(() => {
        if (playingIndex === null) return
        const currentIndex = playingIndex
        const video = videoRefs.current[currentIndex]
        if (!video) return

        const handleTimeUpdate = () => {
            if (video.duration) {
                setProgress(prev => {
                    const newProgress = [...prev]
                    newProgress[currentIndex] = (video.currentTime / video.duration) * 100
                    return newProgress
                })
            }
        }

        const handleEnded = () => {
            setPlayingIndex(null)
            setProgress(prev => {
                const newProgress = [...prev]
                newProgress[currentIndex] = 0
                return newProgress
            })
        }

        video.addEventListener('timeupdate', handleTimeUpdate)
        video.addEventListener('ended', handleEnded)

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate)
            video.removeEventListener('ended', handleEnded)
        }
    }, [playingIndex])

    const handleCardClick = (index: number) => {
        if (activeIndex === index) {
            // Active card - toggle play/pause
            handleTogglePlay(index)
        } else {
            // Inactive card - switch to it
            if (playingIndex !== null) {
                videoRefs.current[playingIndex]?.pause()
                setPlayingIndex(null)
            }
            setActiveIndex(index)
        }
    }

    const handleTogglePlay = useCallback((index: number) => {
        if (!loadedStates[index]) {
            loadVideoSource(index)
        }

        const video = videoRefs.current[index]
        if (!video) return

        if (playingIndex === index) {
            video.pause()
            setPlayingIndex(null)
        } else {
            if (playingIndex !== null && playingIndex !== index) {
                videoRefs.current[playingIndex]?.pause()
            }
            video.play().catch((e) => console.debug('Video play prevented:', e))
            setPlayingIndex(index)
        }
    }, [loadedStates, loadVideoSource, playingIndex])

    const handleFullscreen = (index: number) => {
        const video = videoRefs.current[index]
        const time = video?.currentTime ?? 0

        // Pause video before opening modal
        if (playingIndex !== null) {
            videoRefs.current[playingIndex]?.pause()
            setPlayingIndex(null)
        }

        setModalVideoSources(VIDEOS[index])
        setModalStartTime(time)
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
        setModalVideoSources(null)
        setModalStartTime(0)
    }

    const handleVideoLoading = (index: number, isLoading: boolean) => {
        setLoadingStates(prev => {
            const newStates = [...prev]
            newStates[index] = isLoading
            return newStates
        })
    }

    return (
        <section ref={sectionRef} className="lg:py-[60px] sm:py-10 py-8 px-5 sm:px-6 lg:px-10">
            <div className="max-w-[1340px] mx-auto flex flex-col xl:flex-row gap-6 sm:gap-8 lg:gap-10 items-center justify-between">
                <div className="w-full text-center xl:text-left max-w-[556px]">
                    <h2 className="section-heading">
                        <Trans
                            i18nKey="trusted.heading"
                            ns="home"
                            components={{
                                br1: <br className='sm:hidden lg:block' />,
                                br2: <br className='hidden sm:block lg:hidden' />,
                            }}
                        />
                    </h2>

                    <p className="text-xs sm:text-lg text-[#4A4A4A] mt-2 sm:mt-3.5 xl:mt-4 leading-[20px] sm:leading-[29px]">
                        {t('trusted.body')}
                    </p>
                </div>

                <div className="w-full max-w-[690px] max-h-[550px] aspect-[690/550] flex gap-[18px] lg:gap-[34px] xl:shrink-0 items-end overflow-hidden self-auto">
                    {VIDEOS.map((videoSources, index) => (
                        <div
                            key={index}
                            onClick={() => handleCardClick(index)}
                            className={`
                                relative rounded-[20px] sm:rounded-[26.32px] overflow-hidden cursor-pointer
                                transition-all duration-500 ease-out my-auto
                                ${activeIndex === index
                                    ? 'flex-[356] h-full'
                                    : 'flex-[300] h-[84%] opacity-90 hover:opacity-100'
                                }
                            `}
                        >
                            <video
                                ref={(el) => { videoRefs.current[index] = el }}
                                poster={videoSources.poster}
                                className="w-full h-full object-cover"
                                playsInline
                                preload="none"
                                onWaiting={() => handleVideoLoading(index, true)}
                                onCanPlay={() => handleVideoLoading(index, false)}
                                onLoadedData={() => handleVideoLoading(index, false)}
                            />

                            <VideoControls
                                variant="card"
                                isPlaying={playingIndex === index}
                                isLoading={loadingStates[index]}
                                progress={progress[index]}
                                onTogglePlay={() => handleTogglePlay(index)}
                                onFullscreen={() => handleFullscreen(index)}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <VideoModal
                sources={modalVideoSources}
                isOpen={modalOpen}
                onClose={handleCloseModal}
                startTime={modalStartTime}
            />
        </section>
    )
}

export default TrustedSection
